import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { accessRequestSubmit, type ApiError } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { Wordmark } from '../shell/SideNav';

// The public "Request access" page (the Flutter console's request_access_page.dart). A
// prospect asks for access; the request lands in Aithora's queue (Settings, Users &
// Permissions, Access requests), where it is approved with an invite link or denied.

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const messageOf = (e: unknown) => ((e as ApiError)?.message ? (e as ApiError).message : 'Something went wrong. Try again.');

export function RequestAccess() {
  const { client } = useSession();
  const [f, setF] = useState({ orgName: '', contactName: '', email: '', phone: '', facilityCount: '', roleWanted: 'operator' as 'operator' | 'facility', note: '', pilotCode: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.orgName.trim() || !f.contactName.trim() || !f.email.trim()) return setError('Enter your organization, your name and your work email.');
    if (!EMAIL.test(f.email.trim())) return setError('Enter a valid work email.');
    setBusy(true);
    setError(null);
    try {
      const n = Number(f.facilityCount);
      await accessRequestSubmit(client, {
        body: {
          orgName: f.orgName.trim(),
          contactName: f.contactName.trim(),
          email: f.email.trim(),
          roleWanted: f.roleWanted,
          ...(f.phone.trim() ? { phone: f.phone.trim() } : {}),
          ...(f.facilityCount.trim() && Number.isFinite(n) ? { facilityCount: n } : {}),
          ...(f.note.trim() ? { note: f.note.trim() } : {}),
          ...(f.pilotCode.trim() ? { pilotCode: f.pilotCode.trim() } : {}),
        },
      });
      setSent(true);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <div className="signin__card" data-testid="request-access">
        <Wordmark />
        {sent ? (
          <>
            <h1 className="signin__title">Request sent</h1>
            <p className="signin__lead">Aithora reviews every request. Once it is approved, you get an email with a link to set up your account.</p>
            <div className="signin__links">
              <Link className="linkbtn" to="/signin">
                Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <h1 className="signin__title">Request access</h1>
            <p className="signin__lead">Tell us who you are. Aithora reviews each request before any account is made.</p>
            <label className="field">
              <span className="field__label">Organization</span>
              <input className="field__input" autoComplete="organization" value={f.orgName} onChange={set('orgName')} required />
            </label>
            <label className="field">
              <span className="field__label">Your name</span>
              <input className="field__input" autoComplete="name" value={f.contactName} onChange={set('contactName')} required />
            </label>
            <label className="field">
              <span className="field__label">Work email</span>
              <input className="field__input" type="email" autoComplete="email" value={f.email} onChange={set('email')} required />
            </label>
            <label className="field">
              <span className="field__label">Phone (optional)</span>
              <input className="field__input" type="tel" autoComplete="tel" value={f.phone} onChange={set('phone')} />
            </label>
            <label className="field">
              <span className="field__label">You run</span>
              <select className="field__input" value={f.roleWanted} onChange={(e) => setF((p) => ({ ...p, roleWanted: e.target.value as 'operator' | 'facility' }))}>
                <option value="operator">Several buildings (an operator)</option>
                <option value="facility">One building</option>
              </select>
            </label>
            <label className="field">
              <span className="field__label">How many buildings (optional)</span>
              <input className="field__input" inputMode="numeric" value={f.facilityCount} onChange={set('facilityCount')} />
            </label>
            <label className="field">
              <span className="field__label">Pilot code (optional)</span>
              <input className="field__input" value={f.pilotCode} onChange={set('pilotCode')} />
            </label>
            <label className="field">
              <span className="field__label">Anything we should know (optional)</span>
              <input className="field__input" value={f.note} onChange={set('note')} />
            </label>
            {error && (
              <p className="signin__error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Sending…' : 'Send request'}
            </button>
            <div className="signin__links">
              <Link className="linkbtn" to="/signin">
                Already have an account? Sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
